import { ReactElement, useEffect, useState } from "react";
import {
  Box,
  Flex,
  Grid,
  Text,
  VStack,
  HStack,
  Center,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  useBreakpointValue,
} from "@chakra-ui/react";
import { ReactSVG } from "react-svg";
import { HeaderUserName } from "../components/atoms/CommonLayout/HeaderUserName";
import { BottomNavLayout } from "../components/templates/BottomNavLayout";
import dayjs from "dayjs";
import {
  employeeInfoAtom,
  outputStenScoreAtom,
  hasMi1ResultAtom,
  hasVitalityAtom,
  hasMentalActivityAtom,
  footerIndexAtom,
  detectVolumeRawAtom,
} from "../store";
import {
  ChartInfoType,
  ResultChartData,
  latestMentalActivityAtom,
  latestMentalActivityStringAtom,
  latestStenScoreAtom,
  latestVitalityAtom,
  latestVitalityStringAtom,
  latestMi1ResultReliabilityAtom,
  latestMentalActivityReliabilityAtom,
} from "../stores/mimosysStore";
import { useAtom } from "jotai";
import { LargeRadialChart } from "../components/atoms/ResultCommon/RadialChart";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import {
  homeSplashDisplayAtom,
  homeWeekDataAtom,
  latestMentalActivityTrendAtom,
  latestVitalityTrendAtom,
  mentalActivitySdSetAtom,
  mi1NormalRange,
  recordingLastDatesAtom,
  scoreAverageAtom,
  vitalitySdSetAtom,
} from "../components/atoms/ResultHome/HomeAtoms";

import { Trend } from "../types";
import { HomeChartBody } from "../components/atoms/ResultHome/HomerChart";
import { DownTrendIcon, UpTrendIcon, NormalTrendIcon } from "../assets/Icons";

import { BsQuestionCircleFill } from "react-icons/bs";

import { KOKORO_IMAGE_WITH_SCORE_URL } from "../environments";
import {
  MentalActivityReferenceMarker,
  StenReferenceMarker,
} from "../components/atoms/ResultCommon/StenReferenceMarker";
import { Splash } from "../components/atoms/InnerPage/Splash";
import { useRefreshPhrases } from "../utils/setting";
import { Calibration } from "./calibration";
import { setSVGFillColor } from "../utils/svgHelper";
import { useModifiedTranslation } from "../hooks/useModifiedTranslation";
import { AnalyzedDate } from "../components/atoms/AnalyzedDate";
import { NumericDataText } from "../components/atoms/NumericDataText";
import { ContentContainer } from "../components/atoms/ContentContainer";
import { useColorToken } from "../hooks/useColorToken";

/*
 * ログイン後 ホーム画面（＋解析結果） 過去１週分のグラフ 日付
 */
function displayDate(): string[] {
  const now = dayjs();
  const startDay = dayjs(now).add(-7, "days");
  const endDay = dayjs(now);
  return [
    startDay.format("M/D"),
    startDay.format("(ddd)"),
    endDay.format("M/D"),
    endDay.format("(ddd)"),
  ];
}

/*
 * レイアウトパーツ：「いい調子」「普段通り」「疲れ気味」
 */
function TrendIcon(props: Readonly<{ trend: Trend }>): ReactElement {
  const iconProps = {
    width: "100%",
    height: "auto",
  };

  if (props.trend === Trend.Down) {
    // 「疲れ気味」の矢印
    return <DownTrendIcon {...iconProps} />;
  } else if (props.trend === Trend.Normal) {
    // 「普段通り」の矢印
    return <NormalTrendIcon {...iconProps} />;
  } else {
    // 「いい調子」の矢印
    return <UpTrendIcon {...iconProps} />;
  }
}

/*
 * ホーム画面（＋解析結果） メーター表示の種別（こころ指数／心の活量値）判定
 */
function ScoreMain(
  props: Readonly<{
    outputStenScore: boolean;
  }>,
): ReactElement {
  if (props.outputStenScore) {
    return <Mi1Chart />;
  } else {
    return <MimosysChart />;
  }
}

/*
 * ホーム画面（＋解析結果） メーター表示（心の活量値）
 */
function MimosysChart(): ReactElement {
  const [latestVitality] = useAtom(latestVitalityAtom);
  const [latestVitalityString] = useAtom(latestVitalityStringAtom);
  const [latestMentalActivity] = useAtom(latestMentalActivityAtom);
  const [latestMentalActivityString] = useAtom(latestMentalActivityStringAtom);
  const [latestMentalActivityReliability] = useAtom(
    latestMentalActivityReliabilityAtom,
  );
  const mentalActivityReliability = latestMentalActivityReliability !== false;

  const t = useModifiedTranslation();
  const mentalActivityColor = useColorToken("mentalActivity.primary");
  const vitalityColor = useColorToken("vitality.primary");

  return (
    <Box
      sx={{
        ".carousel .control-dots .dot": {
          backgroundColor: "primary.theme_lv1",
          boxShadow: "none",
          w: 3,
          h: 3,
        },

        ".carousel-slider": {
          pt: 1.25,
          pb: 11.25,
        },
      }}
      w="full"
    >
      <Carousel
        showStatus={false}
        showArrows={false}
        showThumbs={false}
        selectedItem={1}
      >
        {/* 元気圧 */}
        <Box bg="primary.bg_lv1">
          <LargeRadialChart
            progress={latestVitality}
            barColor={vitalityColor}
            score={latestVitalityString}
            title={t("Graph.common.vitality")}
          />
        </Box>
        {/* 心の活量値 */}
        <Box bg="primary.bg_lv1">
          {mentalActivityReliability || (
            <Box>
              <MentalActivityReferenceMarker />
            </Box>
          )}
          <LargeRadialChart
            progress={latestMentalActivity}
            barColor={mentalActivityColor}
            score={latestMentalActivityString}
            title={t("Graph.common.mentalActivity")}
          />
        </Box>
      </Carousel>
    </Box>
  );
}

/*
 * ホーム画面（＋解析結果） メーター表示（こころ指数）
 */
function Mi1Chart(): ReactElement {
  const [latestStenScore] = useAtom(latestStenScoreAtom);
  const [latestMi1ResultReliability] = useAtom(latestMi1ResultReliabilityAtom);

  const stenReliability = latestMi1ResultReliability !== false;

  const textMainLv1 = useColorToken("text.main_text_lv1");
  const meterNeedle = useColorToken("result.meter_needle");
  const meterTextLv1 = useColorToken("result.meter_text_lv1");
  const meterTextLv2 = useColorToken("result.meter_text_lv2");
  const themeLv1 = useColorToken("primary.theme_lv1");
  const themeLv2 = useColorToken("primary.theme_lv2");
  const themeLv3 = useColorToken("primary.theme_lv3");

  return (
    <Flex
      direction="column"
      justifyContent="center"
      alignItems="center"
      width="100%"
    >
      {stenReliability || (
        <Box alignSelf="start">
          <StenReferenceMarker />
        </Box>
      )}
      <Box width="100%" maxWidth="480px">
        <ReactSVG
          src={
            KOKORO_IMAGE_WITH_SCORE_URL[
              latestStenScore === null ? 0 : latestStenScore
            ]
          }
          beforeInjection={(svg) => {
            const title = document.createElementNS(
              "http://www.w3.org/2000/svg",
              "title",
            );
            title.textContent = "kokoro";
            svg.prepend(title);
            svg.setAttribute("width", "100%");
            svg.setAttribute("height", "100%");
            svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
            setSVGFillColor(svg, ".cls-theme1", themeLv1);
            setSVGFillColor(svg, ".cls-theme2", themeLv2);
            setSVGFillColor(svg, ".cls-theme3", themeLv3);
            setSVGFillColor(svg, ".cls-text1", meterTextLv1);
            setSVGFillColor(svg, ".cls-text2", meterTextLv2);
            setSVGFillColor(svg, ".cls-needle", meterNeedle);
            setSVGFillColor(svg, ".cls-mi1Result", textMainLv1);
          }}
          style={{ width: "100%", height: "100%" }}
        />
      </Box>
    </Flex>
  );
}

/*
 * ホーム画面（＋解析結果） 過去１週分のグラフ表示 項目
 */
function HomeChartResultBody(
  props: Readonly<{
    chartInfo: ChartInfoType;
  }>,
): ReactElement {
  const [hasMi1Result] = useAtom(hasMi1ResultAtom);
  const [hasMentalActivity] = useAtom(hasMentalActivityAtom);
  const [hasVitality] = useAtom(hasVitalityAtom);
  const t = useModifiedTranslation();

  const mi1ResultColor = useColorToken("mi1Result.primary");
  const mentalActivityColor = useColorToken("mentalActivity.primary");
  const vitalityColor = useColorToken("vitality.primary");

  return (
    <>
      {/* こころ指数 */}
      {props.chartInfo.outputStenScore ? (
        <ChartResultBody
          title={t("Graph.common.mi1Result")}
          score={props.chartInfo.mi1Ave}
          data={props.chartInfo.data}
          average={props.chartInfo.mi1Ave}
          sdTop={props.chartInfo.mi1NormalRangeTop}
          sdBottom={props.chartInfo.mi1NormalRangeBottom}
          dataKey="mi1_result"
          hasData={hasMi1Result}
          referenceAreaColor={mi1ResultColor}
          tickArea={[1, 10]}
          outputStenScore={props.chartInfo.outputStenScore}
          helpTitleText={t("Graph.tooltipBody.mi1Result")}
          helpChartText={t("Graph.tooltipBody.graph.section")}
        />
      ) : (
        <VStack w="full">
          {/* 元気圧 */}
          <ChartResultBody
            title={t("Graph.common.vitality")}
            score={props.chartInfo.vitalityAve}
            data={props.chartInfo.data}
            average={props.chartInfo.vitalityAve}
            sdTop={props.chartInfo.vitalitySdTop}
            sdBottom={props.chartInfo.vitalitySdBottom}
            dataKey="vitality100"
            hasData={hasMentalActivity}
            referenceAreaColor={vitalityColor}
            tickArea={[0, 100]}
            outputStenScore={props.chartInfo.outputStenScore}
            trend={props.chartInfo.latestVitalityTrend}
            helpTitleText={t("Graph.tooltipBody.vitality")}
            helpChartText={t("Graph.tooltipBody.graph.section")}
          />
          {/* 心の活量値 */}
          <ChartResultBody
            title={t("Graph.common.mentalActivity")}
            score={props.chartInfo.mentalActivityAve}
            data={props.chartInfo.data}
            average={props.chartInfo.mentalActivityAve}
            sdTop={props.chartInfo.mentalActivitySdTop}
            sdBottom={props.chartInfo.mentalActivitySdBottom}
            dataKey="mental_activity100"
            hasData={hasVitality}
            referenceAreaColor={mentalActivityColor}
            tickArea={[0, 100]}
            outputStenScore={props.chartInfo.outputStenScore}
            trend={props.chartInfo.latestMentalActivityTrend}
            helpTitleText={t("Graph.tooltipBody.mentalActivity")}
            helpChartText={t("Graph.tooltipBody.graph.day")}
          />
        </VStack>
      )}
    </>
  );
}

/*
 * ホーム画面（＋解析結果） 過去１週分のグラフ表示 詳細 / 「？」押下時のツールチップ表示
 */
function ChartResultBody(
  props: Readonly<{
    data: ResultChartData[];
    title: string;
    score: number;
    average: number;
    sdTop: number;
    sdBottom: number;
    dataKey: string;
    hasData: boolean;
    referenceAreaColor: string;
    tickArea: number[];
    outputStenScore: boolean;
    helpTitleText?: string;
    helpChartText?: string;
    trend?: Trend;
  }>,
): ReactElement {
  const displayDays = displayDate();

  // 左側の「？」押下時のツールチップ表示 動作定義
  const {
    isOpen: isLeftOpen,
    onOpen: onLeftOpen,
    onClose: onLeftClose,
  } = useDisclosure();
  // 右側の「？」押下時のツールチップ表示 動作定義
  const {
    isOpen: isRightOpen,
    onOpen: onRightOpen,
    onClose: onRightClose,
  } = useDisclosure();

  const t = useModifiedTranslation();

  const aspectRatio = useBreakpointValue({ base: 4 / 1, md: 5 / 1 }) ?? 5 / 1;

  return (
    <Flex alignItems="center" justifyContent="center" w="full">
      <Box
        borderRadius={16}
        boxShadow="md"
        rounded="xl"
        backgroundColor="common.base"
        p={2.5}
        w="full"
      >
        <Modal onClose={onLeftClose} isOpen={isLeftOpen} isCentered>
          {/* 左側の「？」押下時のツールチップ表示  */}
          <ModalOverlay />
          <ModalContent whiteSpace="unset" backgroundColor="common.base">
            <ModalCloseButton />
            <ModalHeader color="text.main_text_lv1">{props.title}</ModalHeader>
            <ModalBody px={6.25} pt={0} pb={8.75}>
              <Text
                whiteSpace="pre-wrap"
                fontWeight="100"
                color="text.main_text_lv1"
              >
                {props.helpTitleText}
              </Text>
            </ModalBody>
          </ModalContent>
        </Modal>

        <Modal onClose={onRightClose} isOpen={isRightOpen} isCentered>
          {/* 右側の「？」押下時のツールチップ表示  */}
          <ModalOverlay />
          <ModalContent whiteSpace="unset" backgroundColor="common.base">
            <ModalCloseButton />
            <ModalHeader color="text.main_text_lv1">
              {t("Graph.tooltipTitle.graph")}
            </ModalHeader>
            <ModalBody px={6.25} pt={0} pb={8.75}>
              <Text
                whiteSpace="pre-wrap"
                fontWeight="200"
                color="text.main_text_lv1"
              >
                {props.helpChartText}
              </Text>
            </ModalBody>
          </ModalContent>
        </Modal>

        <Flex
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          m={2.5}
        >
          {/* 過去１週分のグラフ表示 上部【項目名、ツールチップ用のアイコン、日程の表示】 */}
          <HStack>
            <Text fontWeight="bold" fontSize={{ base: "lg", md: "xl" }}>
              {props.title}
            </Text>
            <Box onClick={onLeftOpen} fontSize="xs" alignSelf="center">
              <BsQuestionCircleFill color="text.main_text_lv1" />
            </Box>
          </HStack>
          <HStack>
            <Flex alignItems="baseline" fontWeight="bold">
              <NumericDataText fontSize="lg" letterSpacing={0.5}>
                {displayDays[0]}
              </NumericDataText>
              <NumericDataText fontSize="sm">{displayDays[1]}</NumericDataText>
              <NumericDataText fontSize="sm" mx={1}>
                -
              </NumericDataText>
              <NumericDataText fontSize="lg" letterSpacing={0.5}>
                {displayDays[2]}
              </NumericDataText>
              <NumericDataText fontSize="sm">{displayDays[3]}</NumericDataText>
            </Flex>
            <Box onClick={onRightOpen} fontSize="xs" alignSelf="center">
              <BsQuestionCircleFill color="text.main_text_lv1" />
            </Box>
          </HStack>
        </Flex>

        <Grid
          templateColumns={
            !props.outputStenScore && props.trend !== undefined
              ? "auto 1fr"
              : "1fr"
          }
          gap={1}
          alignItems="center"
        >
          {/* 「いい調子」「普段通り」「疲れ気味」 */}
          {!props.outputStenScore && props.trend !== undefined && (
            <Box
              gridColumn="1 / span 1"
              display="flex"
              justifyContent="center"
              alignItems="center"
              width={16}
            >
              <TrendIcon trend={props.trend} />
            </Box>
          )}
          <Box
            gridColumn={
              !props.outputStenScore && props.trend !== undefined
                ? "2 / span 1"
                : "1 / span 1"
            }
            minW={0}
            minH={0}
          >
            {/* 過去１週分のグラフ表示 */}
            <HomeChartBody
              data={props.data}
              aspectRatio={aspectRatio}
              reference={{
                lineY: props.average,
                areaTop: props.sdTop,
                areaBottom: props.sdBottom,
                areaColor: props.referenceAreaColor,
              }}
              tickArea={props.tickArea}
              name={props.title}
              dataKey={props.dataKey}
              hasData={props.hasData}
              outputStenScore={props.outputStenScore}
            />
          </Box>
        </Grid>
      </Box>
    </Flex>
  );
}

/*
 * ホーム画面（＋解析結果） 全体表示
 */
function HomeBody(): ReactElement {
  const [_outputStenScore] = useAtom(outputStenScoreAtom);
  const [latestVitalityTrend] = useAtom(latestVitalityTrendAtom);
  const [latestMentalActivityTrend] = useAtom(latestMentalActivityTrendAtom);
  const [data] = useAtom(homeWeekDataAtom);
  const [[vitalityAve, mentalActivityAve, mi1Ave]] = useAtom(scoreAverageAtom);
  const [[vitalitySdTop, vitalitySdBottom]] = useAtom(vitalitySdSetAtom);
  const [[mentalActivitySdTop, mentalActivitySdBottom]] = useAtom(
    mentalActivitySdSetAtom,
  );
  const [mi1NormalRangeTop, mi1NormalRangeBottom] = mi1NormalRange;
  const [recordingLastDates] = useAtom(recordingLastDatesAtom);
  const outputStenScore = _outputStenScore ? _outputStenScore : false;
  const chartInfo: ChartInfoType = {
    data: data,

    latestVitalityTrend: latestVitalityTrend,
    vitalityAve: vitalityAve,
    vitalitySdTop: vitalitySdTop,
    vitalitySdBottom: vitalitySdBottom,

    latestMentalActivityTrend: latestMentalActivityTrend,
    mentalActivityAve: mentalActivityAve,
    mentalActivitySdTop: mentalActivitySdTop,
    mentalActivitySdBottom: mentalActivitySdBottom,

    mi1Ave: mi1Ave,
    mi1NormalRangeTop: mi1NormalRangeTop,
    mi1NormalRangeBottom: mi1NormalRangeBottom,

    recordingLastDates: recordingLastDates,
    outputStenScore: outputStenScore,
  };

  return <VerticalHomeBody chartInfo={chartInfo} />;
}

/*
 * ホーム画面（＋解析結果） 全体表示
 */
export function Home(): ReactElement {
  const [employeeInfo] = useAtom(employeeInfoAtom);
  const [, setFooterIndex] = useAtom(footerIndexAtom);
  const [homeSplashDisplay] = useAtom(homeSplashDisplayAtom);
  const [detectVolumeRaw] = useAtom(detectVolumeRawAtom);
  const refreshPhrases = useRefreshPhrases();

  useEffect(() => {
    setFooterIndex(0);
    refreshPhrases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // NOTE: こころ指数画像のプリロード、もっと良い方法があるかも
  // FIXME: スプラッシュ画面は、ホームに遷移してから表示すべき
  const [outputStenScore] = useAtom(outputStenScoreAtom);
  const [loaded, setLoaded] = useState(!outputStenScore);
  const [latestStenScore] = useAtom(latestStenScoreAtom);

  useEffect(() => {
    if (!homeSplashDisplay && !loaded) {
      const kokoroImageUrl =
        KOKORO_IMAGE_WITH_SCORE_URL[
          latestStenScore === null ? 0 : latestStenScore
        ];

      const img = new Image();
      img.src = kokoroImageUrl;
      img.onload = () => {
        setLoaded(true);
      };
    }
  }, [homeSplashDisplay, loaded, latestStenScore]);

  console.log("detectVolumeRaw", detectVolumeRaw);
  if (homeSplashDisplay || !loaded) {
    return <Splash />;
  } else {
    if (isNaN(detectVolumeRaw)) {
      return <Calibration backButton={false} initialSetup />;
    } else {
      return (
        <BottomNavLayout>
          <ContentContainer flex={1}>
            <HeaderUserName employee_name={employeeInfo.employee_name} />
            <HomeBody />
          </ContentContainer>
        </BottomNavLayout>
      );
    }
  }
}

/*
 * ホーム画面（＋解析結果） 全体表示 縦長版
 */
function VerticalHomeBody(
  props: Readonly<{
    chartInfo: ChartInfoType;
  }>,
): ReactElement {
  return (
    <Center flex={1} flexDirection="column" justifyContent="flex-start">
      <VStack flex={1} w="full" mb={2}>
        <AnalyzedDate />
        <ScoreMain outputStenScore={props.chartInfo.outputStenScore} />
        <HomeChartResultBody chartInfo={props.chartInfo} />
      </VStack>
    </Center>
  );
}
